import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, ArrowSquareOut, Lightning, FileText, ShoppingCart } from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_ORIGIN = 'https://servermemoryrfq.com';

const timeAgo = (iso) => {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return `${Math.floor(diff / (86400 * 30))}mo ago`;
  } catch (e) { return ''; }
};

const upsertHead = (tag, attrs) => {
  const sel = Object.entries(attrs).filter(([k]) => k !== 'content' && k !== 'href')
    .map(([k, v]) => `[${k}="${v}"]`).join('');
  let el = document.head.querySelector(`${tag}${sel}`);
  if (!el) { el = document.createElement(tag); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};
const upsertJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
};

const NewsArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setArticle(null);
    window.scrollTo(0, 0);
    axios.get(`${API_URL}/api/news/${slug}`)
      .then(res => { if (!cancelled) { setArticle(res.data); setLoading(false); } })
      .catch(err => {
        if (cancelled) return;
        if (err.response && err.response.status === 404) setNotFound(true);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const url = `${SITE_ORIGIN}/news/${article.slug}`;
    const title = `${article.title} | ServerMemoryRFQ News`;
    const desc = (article.excerpt || article.title).slice(0, 180);
    document.title = title;
    upsertHead('meta', { name: 'description', content: desc });
    upsertHead('link', { rel: 'canonical', href: url });
    upsertHead('meta', { property: 'og:type', content: 'article' });
    upsertHead('meta', { property: 'og:title', content: article.title });
    upsertHead('meta', { property: 'og:description', content: desc });
    upsertHead('meta', { property: 'og:url', content: url });
    upsertHead('meta', { property: 'article:published_time', content: article.published_at || '' });
    upsertHead('meta', { property: 'article:section', content: article.category || 'Server Memory' });
    upsertHead('meta', { name: 'twitter:card', content: 'summary_large_image' });
    upsertJsonLd('ld-article', {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      '@id': url,
      headline: article.title,
      description: desc,
      url,
      datePublished: article.published_at,
      articleSection: article.category || 'Server Memory',
      publisher: {
        '@type': 'Organization',
        name: 'ServerMemoryRFQ',
        url: SITE_ORIGIN,
      },
      isBasedOn: article.source_url,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    });
  }, [article]);

  if (loading) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#94A3B8] font-mono">Loading article…</div>
        <Footer />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="news-not-found">
          <h1 className="text-3xl font-['JetBrains_Mono'] font-bold mb-4">Article not found</h1>
          <p className="text-[#94A3B8] mb-8">This news story is no longer available.</p>
          <Button onClick={() => navigate('/news')} className="bg-[#0047AB] hover:bg-[#005DF0] font-mono" data-testid="back-to-news-cta">
            Back to all news
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="news-article-page">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back link */}
        <Link to="/news" className="inline-flex items-center gap-2 text-[#007AFF] hover:text-[#005DF0] mb-6 font-mono text-sm" data-testid="back-to-news">
          <ArrowLeft size={16} /> All news
        </Link>

        {/* Main story card */}
        <article className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 sm:p-10 mb-6">
          <div className="flex items-center gap-2 mb-5 text-xs font-mono uppercase tracking-wider flex-wrap">
            <span className="text-cyan-400" data-testid="article-source-domain">{article.source_domain || article.source_name}</span>
            <span className="text-[#334155]">·</span>
            <Clock size={12} className="text-[#94A3B8]" />
            <span className="text-[#94A3B8]">{timeAgo(article.published_at)}</span>
            <span className="text-[#334155]">·</span>
            <span className="text-[#9945FF]">ServerMemoryRFQ</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-['JetBrains_Mono'] font-bold text-white leading-tight mb-6" data-testid="article-title">
            {article.title}
          </h1>

          {article.excerpt && (
            <p className="text-base text-[#94A3B8] leading-relaxed mb-6" data-testid="article-excerpt">
              {article.excerpt}
            </p>
          )}

          {article.blurb && (
            <div className="bg-[#0A1628] border-l-4 border-cyan-500 rounded px-5 py-4 mb-6" data-testid="article-blurb">
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 mb-2">ServerMemoryRFQ Take</div>
              <p className="text-white/90 leading-relaxed">{article.blurb}</p>
            </div>
          )}

          {/* Read source button */}
          <a
            href={article.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-gradient-to-r from-[#0047AB] to-[#005DF0] hover:opacity-90 text-white rounded-lg px-5 py-4 transition-opacity"
            data-testid="read-source-btn"
          >
            <ArrowSquareOut size={20} />
            <span className="font-mono text-sm">Read the full story at {article.source_domain || article.source_name}</span>
          </a>
        </article>

        {/* "If this affects you right now" — SpeedTestTools-style CTA */}
        <section className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 sm:p-8" data-testid="cta-section">
          <div className="flex items-center gap-2 mb-4 text-xs font-mono uppercase tracking-widest text-cyan-400">
            <Lightning size={16} weight="fill" /> If this affects your buying right now
          </div>
          <ul className="space-y-4 mb-6">
            <li className="flex items-start gap-3">
              <ShoppingCart size={20} className="text-[#10B981] flex-shrink-0 mt-0.5" weight="fill" />
              <div className="text-sm text-white/90">
                <strong className="text-white">Lock in stock.</strong> Memory pricing moves fast when supply tightens — if you need any of these SKUs, pin them to a formal quote while they're still available.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <FileText size={20} className="text-[#007AFF] flex-shrink-0 mt-0.5" weight="fill" />
              <div className="text-sm text-white/90">
                <strong className="text-white">Compare live inventory.</strong> Browse our always-fresh database of 2,000+ DDR4, DDR5, HBM and MRDIMM parts with real-time quantity on hand.
              </div>
            </li>
          </ul>
          <div className="grid sm:grid-cols-2 gap-3">
            <Button
              onClick={() => navigate('/rfq')}
              className="bg-[#10B981] hover:bg-[#059669] text-white font-mono font-bold py-6"
              data-testid="cta-request-quote"
            >
              Request a Quote
            </Button>
            <Button
              onClick={() => navigate('/inventory')}
              className="bg-[#0047AB] hover:bg-[#005DF0] text-white font-mono font-bold py-6"
              data-testid="cta-browse-inventory"
            >
              Browse Live Inventory
            </Button>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default NewsArticlePage;
